import { useEffect, useMemo, type ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "configs/store";
import { fetchUserPayments, Payment } from "slices/paymentSlice";
import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import Loader from "components/loader";
import DashboardLayout from "themeComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "themeComponents/Navbars/DashboardNavbar";
import Footer from "themeComponents/Footer";
import Table from "themeComponents/Tables/Table";
import Badge from "themeComponents/Badge";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import useAuth from "hooks/useAuth";

function paymentTypeLabel(p: Payment): string {
  if (p.type === "course") return "Course";
  if (p.type === "subscription") return "Subscription";
  if (p.type === "credits") return "Credits";
  if (p.courseID) return "Course purchase";
  if (p.subscriptionID) return "Subscription";
  return "Other";
}

function Payments(): React.JSX.Element {
  const dispatch = useDispatch();
  const { payments, loading, error } = useSelector((state: RootState) => state.payments);
  const { isAdmin, isCompanyAdmin, isFellowAdmin, isCreator } = useAuth();

  const showPayerColumn = isAdmin || isCompanyAdmin || isFellowAdmin || isCreator;

  useEffect(() => {
    dispatch(fetchUserPayments() as never);
  }, [dispatch]);

  const subtitle = useMemo(() => {
    if (isAdmin) return "All payment transactions recorded on the platform.";
    if (isCompanyAdmin || isFellowAdmin) {
      return "Payments for purchases of courses under your company.";
    }
    if (isCreator) return "Payments for purchases of courses you added or instruct.";
    return "Your payment transactions and subscription history.";
  }, [isAdmin, isCompanyAdmin, isFellowAdmin, isCreator]);

  const columns = useMemo(() => {
    const cols: { name: string; align: "left" | "center" | "right" }[] = [{ name: "date", align: "left" }];
    if (showPayerColumn) cols.push({ name: "payer", align: "left" });
    cols.push(
      { name: "amount", align: "left" },
      { name: "status", align: "center" },
      { name: "method", align: "left" },
      { name: "course", align: "left" },
      { name: "type", align: "left" },
    );
    return cols;
  }, [showPayerColumn]);

  const rows = useMemo(
    () =>
      payments.map((payment: Payment) => {
        const row: Record<string, ReactNode> = {
          date: (
            <Typography variant="caption" fontWeight="medium">
              {payment.createdAt ? new Date(payment.createdAt).toLocaleString() : "—"}
            </Typography>
          ),
          amount: (
            <Typography variant="caption" fontWeight="medium">
              {payment.currency} {Number(payment.amount).toFixed(2)}
            </Typography>
          ),
          status: (
            <Badge
              variant="contained"
              color={getStatusColor(payment.status)}
              badgeContent={payment.status}
              container
            />
          ),
          method: (
            <Typography variant="caption" color="text">
              {payment.paymentMethod || "Stripe"}
            </Typography>
          ),
          course: (
            <Typography variant="caption" color="text">
              {payment.courseTitle || payment.courseID || "—"}
            </Typography>
          ),
          type: (
            <Typography variant="caption" color="text">
              {paymentTypeLabel(payment)}
            </Typography>
          ),
        };
        if (showPayerColumn) {
          row.payer = (
            <Typography variant="caption" color="text" sx={{ wordBreak: "break-all" }}>
              {payment.userID || "—"}
            </Typography>
          );
        }
        return row;
      }),
    [payments, showPayerColumn],
  );

  if (loading && payments.length === 0) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box py={3}>
          <Loader loading />
        </Box>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <Box mb={3}>
          <Typography variant="h4" fontWeight="bold" mb={1}>
            Payment History
          </Typography>
          <Typography variant="body2" color="text">
            {subtitle}
          </Typography>
        </Box>

        {error ? (
          <Box mb={2}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        ) : null}

        {payments.length === 0 ? (
          <Card>
            <CardContent>
              <Typography variant="body1" textAlign="center" color="text">
                No payment history found.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Box
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }: any) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              <Table columns={columns} rows={rows} />
            </Box>
          </Card>
        )}
      </Box>
      <Footer />
    </DashboardLayout>
  );
}

function getStatusColor(status: string): "success" | "warning" | "error" | "info" | "secondary" {
  switch (status) {
    case "completed":
      return "success";
    case "pending":
      return "warning";
    case "failed":
      return "error";
    case "refunded":
      return "info";
    default:
      return "secondary";
  }
}

export default Payments;
